// ============================================================================
// ENUMS AND CONSTANTS
// ============================================================================

export enum LearnerLevel {
  BEGINNER_1 = 1,
  BEGINNER_2 = 2,
  BEGINNER_3 = 3,
  ELEMENTARY_1 = 4,
  ELEMENTARY_2 = 5,
  INTERMEDIATE_1 = 6,
  INTERMEDIATE_2 = 7,
  ADVANCED_1 = 8,
  ADVANCED_2 = 9,
  EXPERT = 10
}

export enum QuizType {
  VOCABULARY = 'vocabulary',
  GRAMMAR = 'grammar',
  PRONUNCIATION = 'pronunciation',
  MIXED = 'mixed'
}

export enum ActivityType {
  /** Progress /stats keys (lessons + block time + quizzes). */
  LESSONS = 'lessons',
  QUIZ = 'quiz',
  /** Course / level / lesson exercise modal sessions (time in progression stats). */
  EXERCISE = 'exercise',
  /** Library book reading time in the book viewer. */
  BOOK = 'book',
  TAGS = 'tags',
  /** Legacy content recommendation types from the API. */
  VOCABULARY = 'vocabulary',
  GRAMMAR = 'grammar',
  DIALOGUE = 'dialogue',
  PRONUNCIATION = 'pronunciation',
  MIXED = 'mixed'
}

export enum ExerciseType {
  MULTIPLE_CHOICE = 'multiple-choice',
  MATCHING = 'matching',
  FLASHCARD = 'flashcard',
  FILL_IN_BLANK = 'fill-in-blank',
  PRONUNCIATION = 'pronunciation'
}

// ============================================================================
// PERSIAN LANGUAGE METADATA
// ============================================================================

export interface PersianMetadata {
  textDirection: 'rtl' | 'ltr';
  languageCode: string;
  recommendedFonts: string[];
  encoding: string;
  containsPersianText: boolean;
  cssProperties?: Record<string, string>;
}

// ============================================================================
// AUTHENTICATION TYPES
// ============================================================================

export interface LoginDto {
  email: string;
  password: string;
  rememberMe?: boolean;
  captchaId?: string;
  captchaCode?: string;
}

export interface RegisterDto {
  email: string;
  password: string;
  confirmPassword: string;
}

export interface AuthResponseDto {
  success: boolean;
  message?: string;
  token?: string;
  expiresAt?: string;
  user?: UserProfileDto;
  requiresCaptcha?: boolean;
  captchaError?: boolean;
}

export interface UserProfileDto {
  id: string;
  email: string;
  phoneNumber?: string;
  phoneNumberConfirmed?: boolean;
  currentLevel: number;
  totalLessonsCompleted: number;
  totalQuizzesTaken: number;
  averageScore: number;
  createdAt: string;
  lastLoginAt: string;
  totalXP?: number;
  streak?: number;
}

export interface UpdateProfileDto {
  email?: string;
  phoneNumber?: string;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// ============================================================================
// VOCABULARY TYPES
// ============================================================================

export interface VocabularyItemDto {
  id: number;
  persianWord: string;
  englishTranslation: string;
  exampleSentencePersian?: string;
  exampleSentenceEnglish?: string;
  audioUrl?: string;
  level: number;
  category: string;
  persianMetadata?: PersianMetadata;
}

export interface VocabularyResponseDto {
  items: VocabularyItemDto[];
  totalCount: number;
  level: number;
  category?: string;
}

export interface VocabularyExerciseDto {
  exerciseId: string;
  questions: VocabularyQuestionDto[];
  timeLimit: number;
  level: number;
  type: ExerciseType;
}

export interface VocabularyQuestionDto {
  id: number;
  persianWord: string;
  options: string[];
  correctAnswer: string;
  audioUrl?: string;
}

export interface VocabularyProgressDto {
  totalWords: number;
  learnedWords: number;
  masteredWords: number;
  currentStreak: number;
  averageScore: number;
  lastPracticed?: string;
}

// ============================================================================
// GRAMMAR TYPES
// ============================================================================

export interface GrammarLessonDto {
  id: number;
  title: string;
  content: string;
  examples: GrammarExampleDto[];
  level: number;
  estimatedDuration: number;
  orderIndex?: number;
  persianMetadata?: PersianMetadata;
}

export interface GrammarExampleDto {
  persian: string;
  english: string;
  explanation?: string;
}

export interface GrammarLessonsResponseDto {
  lessons: GrammarLessonDto[];
  totalCount: number;
  level: number;
}

export interface GrammarExerciseDto {
  id: number;
  type: ExerciseType;
  question: string;
  questionPersian?: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
  points?: number;
}

export interface GrammarExercisesResponseDto {
  exercises: GrammarExerciseDto[];
  lessonId: number;
  totalExercises: number;
}

export interface GrammarProgressDto {
  totalLessons: number;
  completedLessons: number;
  currentLesson?: number;
  averageScore: number;
  lastStudied?: string;
}

// ============================================================================
// QUIZ TYPES
// ============================================================================

export interface QuizDto {
  quizId: string;
  title: string;
  questions: QuizQuestionDto[];
  totalPoints: number;
  timeLimit: number;
  level: number;
  type?: QuizType;
}

export interface QuizQuestionDto {
  id: number;
  type: QuizType;
  question: string;
  questionPersian?: string;
  options: string[];
  correctAnswer: string;
  points: number;
  audioUrl?: string;
  explanation?: string;
}

export interface QuizAnswerDto {
  questionId: number;
  selectedAnswer: string;
  timeSpent?: number;
}

export interface QuizSubmissionDto {
  quizId: string;
  answers: QuizAnswerDto[];
  totalTimeSpent?: number;
}

export interface QuizResultDto {
  quizResultId: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  percentage: number;
  passed: boolean;
  levelAdvancement: LevelAdvancementDto;
  detailedResults: QuizDetailedResultDto[];
  completedAt: string;
}

export interface QuizDetailedResultDto {
  questionId: number;
  correct: boolean;
  selectedAnswer: string;
  correctAnswer: string;
  points: number;
  explanation?: string;
}

export interface QuizStatsDto {
  totalQuizzes: number;
  averageScore: number;
  bestScore: number;
  recentScores: number[];
  passRate: number;
  totalTimeSpent: number;
}

// ============================================================================
// DIALOGUE TYPES
// ============================================================================

export interface DialogueScriptDto {
  id: number;
  title: string;
  description: string;
  level: number;
  estimatedDuration: number;
  lines: DialogueLineDto[];
  scenario?: string;
  persianMetadata?: PersianMetadata;
}

export interface DialogueLineDto {
  id: number;
  speaker: string;
  textPersian: string;
  textEnglish: string;
  audioUrl?: string;
  responses?: DialogueResponseOptionDto[];
}

export interface DialogueResponseOptionDto {
  id: number;
  textPersian: string;
  textEnglish: string;
  audioUrl?: string;
}

export interface DialogueScenariosResponseDto {
  scenarios: DialogueScriptDto[];
  totalCount: number;
  level: number;
}

export interface DialogueResponseDto {
  scenarioId: number;
  lineId: number;
  responseId: number;
  userAudioUrl?: string;
}

export interface DialogueNextLineDto {
  nextLine?: DialogueLineDto;
  feedback?: DialogueFeedbackDto;
  completed?: boolean;
}

export interface DialogueFeedbackDto {
  pronunciationScore?: number;
  suggestions?: string[];
  overallRating?: string;
}

export interface DialogueProgressDto {
  totalScenarios: number;
  completedScenarios: number;
  averageScore: number;
  totalConversations: number;
  lastPracticed?: string;
}

export interface DialoguePracticeSessionDto {
  sessionId: string;
  scenarioId: number;
  currentLineId: number;
  startedAt: string;
  responses: DialogueResponseDto[];
}

export interface DialogueStatsDto {
  totalDialogues: number;
  completedDialogues: number;
  averageScore: number;
  totalConversations: number;
  averageConversationLength: number;
  pronunciationAccuracy: number;
  responseTime: number;
  lastPracticed?: string;
  favoriteScenarios: string[];
  progressByLevel: Record<number, DialogueProgressDto>;
}

// ============================================================================
// AUDIO TYPES
// ============================================================================

export interface AudioUploadDto {
  audioId: string;
  filename: string;
  url: string;
  duration: number;
  format: string;
  size: number;
  uploadedAt: string;
}

export interface AudioMetadataDto {
  filename: string;
  duration: number;
  format: string;
  size: number;
  language?: string;
  quality?: string;
}

export interface PronunciationContentDto {
  id: number;
  word: string;
  phonetic: string;
  audioUrl: string;
  level: number;
  difficulty: string;
}

// ============================================================================
// PROGRESS AND LEVEL TYPES
// ============================================================================

export interface LevelStatusDto {
  currentLevel: number;
  nextLevel: number;
  progressToNext: number;
  requirements: LevelRequirementDto;
  canAdvance: boolean;
  message?: string;
}

export interface LevelRequirementDto {
  minimumScore: number;
  requiredQuizzes: number;
  completedQuizzes: number;
  requiredLessons: number;
  completedLessons: number;
}

export interface LevelAdvancementDto {
  advanced: boolean;
  currentLevel: number;
  nextLevel?: number;
  requiredScore?: number;
  message?: string;
  previousLevel?: number;
  newLevel?: number;
  unlockedContent?: string[];
  congratulationsMessage?: string;
}

export interface ProgressRecordDto {
  activityType: ActivityType;
  contentId: number;
  score: number;
  timeSpent: number;
  completed: boolean;
}

export interface ProgressResponseDto {
  progressId: string;
  xpGained: number;
  newTotalXP: number;
  levelUp: boolean;
  streakContinued: boolean;
  currentStreak: number;
}

// ============================================================================
// DASHBOARD TYPES
// ============================================================================

export interface DashboardStatsDto {
  user: UserStatsDto;
  progress: LearningProgressDto;
  recentActivity: RecentActivityDto[];
  nextRecommendations: ContentRecommendationDto[];
}

export interface UserStatsDto {
  currentLevel: number;
  totalXP: number;
  streak: number;
}

export interface LearningProgressDto {
  vocabularyLearned: number;
  grammarLessonsCompleted: number;
  quizzesTaken: number;
  dialoguesCompleted: number;
  averageScore: number;
}

export interface RecentActivityDto {
  type: ActivityType;
  title: string;
  score?: number;
  completedAt: string;
  level?: number;
}

export interface ContentRecommendationDto {
  type: ActivityType;
  title: string;
  level: number;
  estimatedDuration: number;
  contentId?: number;
}

// ============================================================================
// ADMIN TYPES
// ============================================================================

export interface AdminLoginDto {
  email: string;
  password: string;
  captchaId?: string;
  captchaCode?: string;
}

export interface AdminAuthResponseDto {
  success: boolean;
  message?: string;
  token?: string;
  expiresAt?: string;
  user?: AdminUserDto;
  requiresCaptcha?: boolean;
  captchaError?: boolean;
}

export interface AdminUserDto {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

export interface CreateUserDto {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  isActive: boolean;
}

export interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  isActive?: boolean;
}

export interface AssignRoleDto {
  userId: string;
  roleNames: string[];
}

export interface RoleDto {
  id?: number;
  name: string;
  description?: string;
  permissions?: string[];
}

export interface AdminDashboardDto {
  systemHealth: ApiHealthDto;
  performanceMetrics: PerformanceMetricsDto;
  userStatistics: UserStatisticsDto;
  contentStatistics: ContentStatisticsDto;
}

export interface ApiHealthDto {
  status: string;
  timestamp: string;
  recentRequests: number;
  averageResponseTime: number;
  cacheHitRate: number;
  databaseQueryCount: number;
  failedQueries: number;
  errorMessage?: string;
  checks?: HealthCheckDto[];
  totalResponseTime?: string;
}

export interface HealthCheckDto {
  name: string;
  status: string;
  responseTime: string;
}

export interface PerformanceMetricsDto {
  generatedAt: string;
  timeRange: string;
  apiMetrics: ApiMetricsDto;
  databaseMetrics: DatabaseMetricsDto;
  cacheMetrics: CacheMetricsDto;
}

export interface ApiMetricsDto {
  totalRequests: number;
  averageResponseTime: number;
  errorRate: number;
  requestsPerMinute: number;
}

export interface DatabaseMetricsDto {
  totalQueries: number;
  averageQueryTime: number;
  slowQueries: number;
  connectionPoolSize: number;
}

export interface CacheMetricsDto {
  hitRate: number;
  missRate: number;
  totalKeys: number;
  memoryUsage: number;
}

export interface UserStatisticsDto {
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  averageLevel: number;
  retentionRate: number;
}

export interface ContentStatisticsDto {
  totalVocabulary: number;
  totalGrammarLessons: number;
  totalDialogues: number;
  totalQuizzes: number;
  contentByLevel: Record<number, ContentLevelStatsDto>;
}

export interface ContentLevelStatsDto {
  vocabulary: number;
  grammar: number;
  dialogues: number;
  quizzes: number;
}

export interface UserAnalyticsDto {
  totalUsers: number;
  activeUsers: number;
  usersByLevel: Record<number, number>;
  engagementMetrics: EngagementMetricsDto;
  learningPatterns: LearningPatternDto[];
}

export interface EngagementMetricsDto {
  averageSessionDuration: number;
  averageActivitiesPerSession: number;
  retentionRates: RetentionRateDto;
  mostPopularContent: PopularContentDto[];
}

export interface RetentionRateDto {
  day1: number;
  day7: number;
  day30: number;
}

export interface PopularContentDto {
  type: ActivityType;
  title: string;
  usageCount: number;
  averageScore: number;
}

export interface LearningPatternDto {
  timeOfDay: string;
  activityType: ActivityType;
  frequency: number;
  averageScore: number;
}

// ============================================================================
// SEEDING TYPES
// ============================================================================

export interface SeedingResponseDto {
  message: string;
  seededData?: SeededDataDto;
  itemsCreated?: number;
  levels?: number[];
  categories?: string[];
  seededAt: string;
}

export interface SeededDataDto {
  vocabulary: number;
  grammarLessons: number;
  dialogues: number;
  quizzes: number;
}

export interface SeedingStatus {
  status: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
  progress?: number;
}

export interface SeedingLogEntry {
  timestamp: string;
  type: string;
  message: string;
  success: boolean;
}

// ============================================================================
// UI STATE TYPES
// ============================================================================

export interface UIState {
  activeSection: string;
  loading: Record<string, boolean>;
  errors: Record<string, string>;
  notifications: NotificationDto[];
  persianSettings: PersianUISettings;
}

export interface NotificationDto {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  persianText?: boolean;
}

export interface PersianUISettings {
  fontFamily: string;
  fontSize: number;
  textDirection: 'rtl' | 'ltr' | 'auto';
  enablePersianDigits: boolean;
  enableBidirectionalText: boolean;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  timestamp?: string;
  persianMetadata?: PersianMetadata;
}

// ============================================================================
// PAGINATION TYPES
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// ERROR TYPES
// ============================================================================

export interface ApiErrorDto {
  message: string;
  details?: string;
  statusCode: number;
  timestamp: string;
  traceId?: string;
  errors?: Record<string, string[]>;
  suggestions?: string[];
}

export interface ValidationErrorDto {
  field: string;
  message: string;
  code: string;
}

// ============================================================================
// LEGACY COMPATIBILITY (for existing code)
// ============================================================================

export interface UserProgress {
  level: LearnerLevel;
  completedLessons: number;
  vocabularyScore: number;
  grammarScore: number;
  pronunciationScore: number;
  quizScores: number[];
  totalScore: number;
  streakDays: number;
  lastLoginDate: string;
  achievements: string[];
}

export interface VocabularyWord {
  id: string;
  persian: string;
  english: string;
  pronunciation: string;
  category: string;
  level: LearnerLevel;
  audioUrl?: string;
}

export interface GrammarLesson {
  id: string;
  title: string;
  content: string;
  examples: Array<{
    persian: string;
    english: string;
  }>;
  exercises: Array<{
    question: string;
    options: string[];
    correctAnswer: number;
  }>;
  level: LearnerLevel;
}

export interface QuizQuestion {
  id: string;
  type: 'vocabulary' | 'grammar' | 'pronunciation';
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  level: LearnerLevel;
}

// ============================================================================
// SUBSCRIPTION TYPES (Re-exported from subscription.ts)
// ============================================================================

export * from './subscription';

// ============================================================================
// API OPTIMIZATION TYPES (Re-exported from apiOptimization.ts)
// ============================================================================

export * from './apiOptimization';